import React, { useContext, useEffect, useState } from 'react';
import {
  Container, Row, Col, Card, Form, Button, CloseButton
} from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  doc, getDoc, collection, addDoc, updateDoc, serverTimestamp, getDocs, query, where, limit
} from 'firebase/firestore';
import { db } from '../FireBase';
import { AuthContext } from '../context/AuthContext';
import GuestFormModal from '../components/GuestFormModal';
import CouponPopup from '../components/CouponPopup';
import SuccessModal from '../components/SuccessModal';
import initiateRazorpayPayment from '../components/Razorpay';
import PaymentLoading from '../components/PaymentLoading';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';

import {
  Users, Plus, Receipt, TicketPercent, CalendarClock, IndianRupee, LoaderCircle, Wallet, TentTree
} from 'lucide-react';

export default function PackageCheckoutPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);

  const bookingState = location.state || {};

  const [pkg, setPkg] = useState(bookingState.packageData || null);
  const [guestList, setGuestList] = useState([]);
  const [range, setRange] = useState({
    from: bookingState.startDate ? new Date(bookingState.startDate) : null,
    to: bookingState.endDate ? new Date(bookingState.endDate) : null,
  });

  const [selectedMembers, setSelectedMembers] = useState(Number(bookingState.members || 1));
  const [selectedDays, setSelectedDays] = useState(Number(bookingState.days || 1));
  const [pricePerPerson, setPricePerPerson] = useState(Number(bookingState.pricePerPerson || 0));

  const [showGuestModal, setShowGuestModal] = useState(false);
  const [fetchedCoupons, setFetchedCoupons] = useState([]);
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState(0);
  const [showCoupon, setShowCoupon] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchPackage();
    fetchCoupons();
  }, [currentUser]);

  const fetchPackage = async () => {
    if (bookingState.packageData) return;

    try {
      const ref = doc(db, 'packages', id);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setPkg(data);

        if (!pricePerPerson) {
          setPricePerPerson(Number(data.price || data.pricePerPerson || 0));
        }

        if (!selectedMembers) {
          setSelectedMembers(Number(data.minMembers || 1));
        }

        if (!selectedDays) {
          setSelectedDays(Number(data.minDays || 1));
        }
      }
    } catch (error) {
      console.error('Error fetching package:', error);
      toast.error('Failed to load package details.');
    }
  };

  const fetchCoupons = async () => {
    try {
      const q = query(collection(db, 'coupons'), where('active', '==', true));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFetchedCoupons(data);
    } catch (error) {
      console.error('Error fetching coupons:', error);
      toast.error('Failed to load coupons.');
    }
  };

  const formatDate = (date) =>
    date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const formatDateForOrder = (date) => {
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const baseTotal = pricePerPerson * selectedMembers * selectedDays;
  const total = Math.max(baseTotal - discount, 0);

  const applyCoupon = () => {
    const code = coupon.trim().toUpperCase();
    const match = fetchedCoupons.find(c => c.code?.toUpperCase() === code);

    if (!match) {
      setDiscount(0);
      toast.error('Invalid or expired coupon.');
      return;
    }

    setDiscount(Number(match.discountValue || 0));
    setShowCoupon(false);
    toast.success(`Coupon "${match.code}" applied!`);
  };

  const handleRemoveGuest = (i) => {
    setGuestList(prev => prev.filter((_, index) => index !== i));
    toast.success('Guest removed');
  };

  const handleBookPackage = async () => {
    if (!range.from || !range.to) {
      toast.error('Please select your travel dates.');
      return;
    }

    if (guestList.length === 0) {
      toast.error('Please add at least one guest.');
      return;
    }

    

    const orderId = 'PKG' + uuidv4().slice(0, 8).toUpperCase();
    const transactionId = 'TR' + Date.now().toString().slice(-6);

    const bookingData = {
      userId: currentUser.cusmid,
      orderId,
      createdAt: new Date().toISOString(),
      packageId: pkg.id,
      packageName: pkg.name,
      pricePerPerson,
      selectedDays,
      selectedMembers,
      subtotal: baseTotal,
      discount,
      total,
      paymentMethod,
      guests: guestList,
      persons: guestList.length,
      checkIn: formatDateForOrder(range.from),
      checkOut: formatDateForOrder(range.to),
      stayDays: selectedDays,
      status: 'payment-pending',
      bookingType: 'online',
      paymentStatus: 'Pending',
      packageImage: pkg?.images?.[0] || '',
    };

    const transactionData = {
      userId: currentUser.cusmid,
      orderId,
      transactionId,
      paymentAmount: total,
      paymentDate: serverTimestamp(),
      type: 'PACKAGE_BOOKING',
    };

    try {
      setLoading(true);

      await addDoc(collection(db, 'package_bookings'), bookingData);
      await addDoc(collection(db, 'transactions'), transactionData);

      let razorpayId = '';

      if (paymentMethod === 'razorpay') {
        const success = await initiateRazorpayPayment(
          total,
          transactionId,
          currentUser.phoneNumber,
          orderId
        );

        if (!success) throw new Error('Payment failed');

        razorpayId = success.razorpay_payment_id;

        const q = query(
          collection(db, 'transactions'),
          where('orderId', '==', orderId),
          limit(1)
        );

        const snap = await getDocs(q);

        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, {
            razorpayId,
            status: 'paid',
          });
        }
      }

      if (coupon) {
        const matched = fetchedCoupons.find(
          c => c.code?.toUpperCase() === coupon.trim().toUpperCase()
        );

        if (matched) {
          const cref = doc(db, 'coupons', matched.id);
          const cdoc = await getDoc(cref);
          const usage = cdoc.data()?.userUsage || {};
          const currentCount = usage[currentUser.cusmid] || 0;

          await updateDoc(cref, {
            [`userUsage.${currentUser.cusmid}`]: currentCount + 1
          });

          await addDoc(collection(db, 'coupon_history'), {
            couponCode: matched.code,
            couponId: matched.id,
            userId: currentUser.cusmid,
            orderId,
            usedAt: serverTimestamp()
          });
        }
      }

      toast.success('Booking placed successfully!');
      setOrderPlaced(true);
    } catch (err) {
      console.error(err);
      toast.error('Error placing booking.');
    } finally {
      setLoading(false);
    }
  };

  if (!pkg) return <p className="text-center mt-5">Loading package...</p>;

  return (
    <Container className="my-4" style={{ paddingBottom: 60 }}>
      {orderPlaced && <SuccessModal show onClose={() => navigate('/')} />}
      {loading && <PaymentLoading show />}

      <center><h1 style={{ color: '#54b435' }}>Package Booking</h1></center>
      <br />

      <Row>
        <Col md={7}>
          <Card className="mb-3 shadow-sm room-card-checkout">
            <Card.Img
              src={pkg?.images?.[0]}
              style={{ objectFit: 'cover', height: 250, borderRadius: '15px 15px 0px 0px' }}
            />
            <Card.Body>
              <h5 className="fw-bold">{pkg.name}</h5>
              <p><IndianRupee size={18} /> ₹{pricePerPerson.toFixed(2)} / person / day</p>
              <p><CalendarClock size={18} /> {formatDate(range.from)} → {formatDate(range.to)}</p>
              <p><TentTree size={18} /> Stay: {selectedDays} {selectedDays === 1 ? 'Day' : 'Days'}</p>
              <p><Users size={18} /> Guests: {selectedMembers}</p>
            </Card.Body>
          </Card>

          <Card className="mb-3 shadow-sm room-card-checkout">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <h5><Users size={18} /> Guests</h5>
                <Button
                  size="sm"
                  className="btn-own-2"
                  onClick={() => setShowGuestModal(true)}
                  disabled={guestList.length >= selectedMembers}
                >
                  <Plus size={16} /> Add Guest
                </Button>
              </div>

              <ul className="mt-2 ps-3">
                {guestList.length === 0 ? (
                  <li className="text-muted">No guest details found</li>
                ) : (
                  guestList.map((guest, i) => (
                    <li key={i} className="d-flex justify-content-between align-items-center">
                      {guest.name} ({guest.phone})
                      <CloseButton onClick={() => handleRemoveGuest(i)} />
                    </li>
                  ))
                )}
              </ul>

              <small className="text-muted">
                Add {selectedMembers} {selectedMembers === 1 ? 'guest' : 'guests'} for this booking.
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="mb-3 shadow-sm room-card-checkout">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <strong><TicketPercent size={18} /> Have a coupon?</strong>
                {discount > 0 && <span className="text-success">Applied</span>}
              </div>
              <Button size="sm" className="btn-own-2" onClick={() => setShowCoupon(true)}>
                Add
              </Button>
            </Card.Body>
          </Card>

          <Card className="mb-3 shadow-sm room-card-checkout">
            <Card.Body>
              <h5><Receipt size={18} /> Order Summary</h5>
              <p>Price per person per day: ₹{pricePerPerson.toFixed(2)}</p>
              <p>Guests: {selectedMembers}</p>
              <p>Days: {selectedDays}</p>
              <p>Subtotal: ₹{baseTotal.toFixed(2)}</p>
              <p>Discount: ₹{discount.toFixed(2)}</p>
              <h6 className="fw-bold">Total: ₹{total.toFixed(2)}</h6>
            </Card.Body>
          </Card>

          <Card className="mb-3 shadow-sm room-card-checkout">
            <Card.Body>
              <h5><Wallet size={18} /> Payment Method</h5>
              <Form.Check
                type="radio"
                label="Razorpay (UPI / Card / Wallets)"
                name="payment"
                value="razorpay"
                checked={paymentMethod === 'razorpay'}
                onChange={e => setPaymentMethod(e.target.value)}
              />
            </Card.Body>
          </Card>

          <Button
            className="w-100 d-flex align-items-center justify-content-center gap-2 btn-own"
            onClick={handleBookPackage}
            disabled={loading}
            style={{ height: '50px' }}
          >
            {loading ? (
              <>
                <LoaderCircle className="spin" size={16} /> Processing...
              </>
            ) : (
              '💳 Make Payment'
            )}
          </Button>
        </Col>
      </Row>

      <CouponPopup
        show={showCoupon}
        handleClose={() => setShowCoupon(false)}
        isMobile={window.innerWidth < 768}
        coupon={coupon}
        setCoupon={setCoupon}
        applyCoupon={applyCoupon}
        availableCoupons={fetchedCoupons}
      />

      <GuestFormModal
        show={showGuestModal}
        handleClose={() => setShowGuestModal(false)}
        guestList={guestList}
        setGuestList={setGuestList}
        maxGuests={selectedMembers}
      />
    </Container>
  );
}